import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { useHistory } from 'react-router-dom'
import ReactApexChart from 'react-apexcharts'
import Pagination from '../components/Pagination'
import TooltipMaxLength from '../../hooks/utils/TooltipMaxLength'
import { getMembershipFinancialOverview } from '../../services/financialService'
import { formatCurrencyUSD } from '../../helpers/FormatCurrency'
import { getMyPayments } from '../../services/loyaltyProgramService'
import { formatDateShortIntl2 } from '../../helpers/FormatDate'
import { PaymentHistory } from '../profileMembers/components/creditsAndPayments/CreditsAndPaymentsWrapper'

const FinancialPage: FC = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const history = useHistory()

    const [filterSelected, setFilterSelected] = useState<string>('all_time');
    const [graphics, setGraphics] = useState<any>();
    const limit = 10;
    
    const [receipts, setReceipts] = useState<PaymentHistory[]>([]);
    const [currentPagePayment, setCurrentPagePayment] = useState(1)
    const [paginationDataPayment, setPaginationDataPayment] = useState<any>(null);
    const totalPagesPayment = paginationDataPayment ? Math.ceil(paginationDataPayment.count / limit) : 1;
    
    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        })

        getFinancialData()
        getPayments()
        return () => setLayout(restoreRef.current)
    }, [])

    const getFinancialData = async (filter: string = 'all_time') => {
        setFilterSelected(filter)
        let credits = await getMembershipFinancialOverview({ filter })
        setGraphics(credits)
    }

    const getPayments = async (page:number=1,offset:number=0) => {
        // Member's own receipts from the loyalty ledger read API (member/payments/); no user_id → self.
        const payments = await getMyPayments({ limit, offset })
        setPaginationDataPayment(payments);
        setReceipts(payments?.results || []);
        setCurrentPagePayment(page);
    }

    /* saved */
    const savedChartOptions = {
        chart: { type: 'donut' },
        labels: ['Expired', 'Soon', 'Spent'],
        colors: ['#540008', '#ccc', '#eee'],
        legend: { show: false },
        dataLabels: { enabled: false },
    }

    /* earned */
    const earnedOptions = {
        chart: { type: 'donut' },
        labels: ['Experiences', 'Expertise'],
        colors: ['#978B63', '#163944'],
        legend: { show: false },
        dataLabels: { enabled: false },
    }

    /* spent */
    const spentOptions = {
        chart: { type: 'donut' },
        labels: ['Experiences', 'Expertise', 'Introductions'],
        colors: ['#978B63','#163944','#460107'],
        legend: { show: false },
        dataLabels: { enabled: false },
    }

    const handlePageChangePayment = async (page: number) => {
        const offset = (page - 1) * limit;
        await getPayments(page, offset);
    };

    return (
        <div className="financial-page">
            <div className="financial-membership">
                <div className="financial-membership__container">

                    {/* HEADER */}
                    <div className="d-flex flex-row justify-content-between align-items-center">
                        <h1 className="financial-membership__title">Financial Overview</h1>
                        <div className="time-filter">
                            <div className={`time-filter__item time-filter__item${filterSelected === 'all_time' ? '--active' : ''}`} onClick={() => getFinancialData('all_time')}>
                                <span className="time-filter__text">
                                    All times
                                </span>
                            </div>
                            <div className='financial-separator-tabs'></div>
                            <div className={`time-filter__item time-filter__item${filterSelected === 'this_year' ? '--active' : ''}`} onClick={() => getFinancialData('this_year')}>
                                <span className="time-filter__text">
                                    This year
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="financial-membership__loyalty-content gap-3">
                        {/* card saved credits */}
                        <div className="left financial-membership__card">
                            <div className="w-100 financial-membership__credits">
                                <ReactApexChart
                                    options={savedChartOptions}
                                    series={[(graphics?.savedCredits?.expired || 0), (graphics?.savedCredits?.expiringSoon || 0), (graphics?.savedCredits?.spent || 0)]}
                                    type="donut"
                                    height={220}
                                />

                                <div className="center-text">
                                    <span className="w-100">Saved</span>
                                    <strong className="w-100">{(graphics?.savedCredits?.total || 0)}</strong>
                                    <span>
                                        <img src="/media/laurel-wreath.png" height={27} width={27} alt="" />
                                    </span>
                                </div>
                            </div>

                            <div className="financial-membership__info">
                                <div className="credits-info">
                                    <div className="credits-info__title">
                                        1 Credit = 1 USD <br />
                                    </div>


                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Available Credits
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD((graphics?.savedCredits?.spent || 0),0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Credits Earned
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD((graphics?.savedCredits?.total || 0),0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Credits Expired
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD((graphics?.savedCredits?.expired || 0),0)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className='financial-membership__cancel-subscription'>
                                        Loyalty Program
                                    </div>

                                    <div className="credits-info__description">                                        
                                        Credit Rollover (Excess over 25K):<br/><br/>
                                        Up to $25K → 1:1<br/>
                                        $25K–$75K → 1:0.5<br/>
                                        $75K+ → 0 (No rollover)
                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                        {/* card earned */}
                        <div className="left financial-membership__card">
                            <div className="financial-membership__credits">
                                <ReactApexChart
                                    options={earnedOptions}
                                    series={[(graphics?.earned?.experiencesTotal || 0), (graphics?.earned?.expertiseTotal || 0)]}
                                    type="donut"
                                    height={220}
                                />

                                <div className="center-text">
                                    <span className='in-graphic-label'>Earned</span>
                                    <strong className='in-graphic-number'>{formatCurrencyUSD((graphics?.earned?.total || 0),0)}</strong>
                                    <span>Available Credits</span>
                                </div>
                            </div>

                            <div className="financial-membership__info">
                                <div className="credits-info">
                                    <div className="item-graphic-legend">
                                        <div className="item-graphic-legend__item">
                                            <div className="item-graphic-legend__color item-graphic-legend__color--experience"></div>
                                            <div className="item-graphic-legend__text">Experiences</div>
                                        </div>

                                        <div className="item-graphic-legend__item">
                                            <div className="item-graphic-legend__color item-graphic-legend__color--expertise"></div>
                                            <div className="item-graphic-legend__text">Expertise</div>
                                        </div>
                                    </div>

                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Hosted Experiences
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD(graphics?.earned?.experiencesTotal || 0, 0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Your Expertise
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD(graphics?.earned?.expertiseTotal || 0, 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* card spent*/}
                        <div className="left financial-membership__card">
                            <div className="financial-membership__credits">
                                <ReactApexChart
                                    options={spentOptions}
                                    series={[(graphics?.spent?.experiencesTotal || 0), (graphics?.spent?.expertiseTotal || 0), (graphics?.spent?.introductionsTotal || 0)]}
                                    type="donut"
                                    height={220}
                                />

                                <div className="center-text">
                                    <span className="in-graphic-label">Spent</span>
                                    <strong className="in-graphic-number">{formatCurrencyUSD(graphics?.earned?.total || 0, 0)}</strong>
                                </div>
                            </div>

                            <div className="financial-membership__info">
                                <div className="credits-info">
                                    <div className="item-graphic-legend">
                                        <div className="item-graphic-legend__item">
                                            <div className="item-graphic-legend__color item-graphic-legend__color--experience"></div>
                                            <div className="item-graphic-legend__text">Experiences</div>
                                        </div>
                                        <div className="item-graphic-legend__item">
                                            <div className="item-graphic-legend__color item-graphic-legend__color--expertise"></div>
                                            <div className="item-graphic-legend__text">Expertise</div>
                                        </div>
                                        <div className="item-graphic-legend__item">
                                            <div className="item-graphic-legend__color item-graphic-legend__color--introduction"></div>
                                            <div className="item-graphic-legend__text">Introductions</div>
                                        </div>
                                    </div>

                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Experiences
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD(graphics?.spent?.experiencesTotal || 0, 0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Expertise
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD(graphics?.spent?.expertiseTotal || 0, 0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="membership-contribution">
                                        <div className="membership-contribution__left">
                                            <div className="membership-contribution__label">
                                                Introductions
                                            </div>
                                        </div>

                                        <div className="membership-contribution__right">
                                            <div className="membership-contribution__amount">
                                                {formatCurrencyUSD(graphics?.spent?.introductionsTotal || 0, 0)}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ACTIVITY */}
            <div className='w-100'>
                <div className="payments-method__info">
                    <span className="payments-method__subtitle">
                        Receipts
                    </span>
                </div>
                <div className="receipts-task-table">
                    <div className="receipts-task-table__header">
                        <div>Date</div>
                        <div>Item</div>
                        <div>Method</div>
                        <div>Beneficiary</div>
                        <div>Amount</div>
                        {/* <div>Action</div> */}
                    </div>

                    {receipts.map((row, index) => (
                        <div key={index} className="receipts-task-table__row">
                            <div className="receipts-task-table__cell">
                                {formatDateShortIntl2(row.date)}
                            </div>
                            <div className="receipts-task-table__cell">
                                <TooltipMaxLength
                                    text={row.item}
                                    maxLength={80}
                                    maxWidth={380}
                                    position="bottom"
                                />
                            </div>
                            <div className="receipts-task-table__cell">
                                {row.method}
                            </div>
                            <div className="receipts-task-table__cell">
                                <span>{row.beneficiary}</span>
                            </div>
                            <div className="receipts-task-table__cell">
                                {row.amount}
                            </div>
                            {/* <div className="receipts-task-table__cell">
                                Action
                            </div> */}
                        </div>
                    ))}
                    <div className='w-100 mt-4'>
                        <Pagination currentPage={currentPagePayment} totalPages={totalPagesPayment} onPageChange={handlePageChangePayment} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export { FinancialPage }